"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage, getInitials } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import UserSearchInput from "@/components/ui/user-search-input";
import { createPlaceholderContributor } from "@/services/admin";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Check, X, AlertTriangle, MessageSquare, Plus, UserPlus, Percent,
  Shield, Loader2, Send, ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import ClaimCreditsButton from "@/components/claim-credits-button";
import { useToast } from "@/hooks/use-toast";
import {
  claimCredit, getCreditsForMedia, approveCredit, rejectCredit,
  createDispute, getDisputesForMedia, addDisputeComment, resolveDispute,
  adminAttachUserToMedia, subscribeToMediaCredits, subscribeToMediaDisputes,
  type MediaCredit, type CreditDispute,
} from "@/services/media-credits";
import { cn } from "@/lib/utils";
import { useContributorRoles } from "@/hooks/use-app-config";

interface MediaCreditsPanelProps {
  mediaId: string;
  mediaType: 'release' | 'work' | 'video';
  mediaTitle: string;
  /** For releases — passed through so the unified ClaimCreditsButton dialog
   *  can show the per-track picker. */
  tracks?: { workId: string; title: string; trackNumber?: number; audioUrl?: string; mp3Url?: string }[];
  existingCredits?: { role: string; name: string }[];
}

export function MediaCreditsPanel({ mediaId, mediaType, mediaTitle, existingCredits, tracks }: MediaCreditsPanelProps) {
  const { items: ROLE_OPTIONS } = useContributorRoles();
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [credits, setCredits] = useState<MediaCredit[]>([]);
  const [disputes, setDisputes] = useState<CreditDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const [expandedDispute, setExpandedDispute] = useState<string | null>(null);

  // Claim form
  const [claimRole, setClaimRole] = useState('');
  const [claimSplit, setClaimSplit] = useState('');
  const [claimNotes, setClaimNotes] = useState('');
  const [claiming, setClaiming] = useState(false);

  // Dispute form
  const [disputeCreditId, setDisputeCreditId] = useState('');
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeProposedRole, setDisputeProposedRole] = useState('');
  const [disputeProposedSplit, setDisputeProposedSplit] = useState('');
  const [disputing, setDisputing] = useState(false);

  // Attach user form (admin)
  const [attachUserId, setAttachUserId] = useState('');
  const [attachUserName, setAttachUserName] = useState('');
  const [attachRole, setAttachRole] = useState('');
  const [attachSplit, setAttachSplit] = useState('');
  const [attaching, setAttaching] = useState(false);

  // Comment form
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);

  const isAdmin = userProfile?.roles?.includes('admin') || userProfile?.roles?.includes('superadmin') || userProfile?.accountType === 'admin' || userProfile?.accountType === 'superadmin';

  // Subscribe to real-time updates
  useEffect(() => {
    if (!mediaId) return;
    setLoading(true);
    const unsubCredits = subscribeToMediaCredits(mediaId, (c) => {
      setCredits(c);
      setLoading(false);
    });
    const unsubDisputes = subscribeToMediaDisputes(mediaId, setDisputes);
    return () => {
      unsubCredits();
      unsubDisputes();
    };
  }, [mediaId]);

  const handleClaimCredit = async () => {
    if (!user?.uid || !claimRole) return;
    setClaiming(true);
    try {
      await claimCredit({
        mediaId,
        mediaType,
        mediaTitle,
        userId: user.uid,
        userName: userProfile?.displayName || 'User',
        userAvatar: userProfile?.profileImageUrl,
        role: claimRole,
        splitPercent: claimSplit ? parseFloat(claimSplit) : undefined,
        status: 'pending',
        claimedBy: user.uid,
        claimedByName: userProfile?.displayName || 'User',
        notes: claimNotes || undefined,
      });
      toast({ title: "Credit Claimed", description: "Your credit claim has been submitted for review." });
      setShowClaimDialog(false);
      setClaimRole('');
      setClaimSplit('');
      setClaimNotes('');
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to submit claim." });
    } finally {
      setClaiming(false);
    }
  };

  const handleApprove = async (creditId: string) => {
    if (!user?.uid) return;
    try {
      await approveCredit(creditId, user.uid);
      toast({ title: "Credit Approved" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to approve." });
    }
  };

  const handleReject = async (creditId: string) => {
    try {
      await rejectCredit(creditId);
      toast({ title: "Credit Rejected" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to reject." });
    }
  };

  const handleCreateDispute = async () => {
    if (!user?.uid || !disputeCreditId || !disputeReason) return;
    setDisputing(true);
    try {
      await createDispute({
        creditId: disputeCreditId,
        mediaId,
        disputedBy: user.uid,
        disputedByName: userProfile?.displayName || 'User',
        reason: disputeReason,
        proposedRole: disputeProposedRole || undefined,
        proposedSplitPercent: disputeProposedSplit ? parseFloat(disputeProposedSplit) : undefined,
        status: 'open',
      });
      toast({ title: "Dispute Filed", description: "Your dispute has been submitted." });
      setShowDisputeDialog(false);
      setDisputeReason('');
      setDisputeProposedRole('');
      setDisputeProposedSplit('');
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to file dispute." });
    } finally {
      setDisputing(false);
    }
  };

  const handleAddComment = async (disputeId: string) => {
    if (!user?.uid || !commentText.trim()) return;
    setCommenting(true);
    try {
      await addDisputeComment(disputeId, user.uid, userProfile?.displayName || 'User', userProfile?.profileImageUrl, commentText.trim());
      setCommentText('');
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to add comment." });
    } finally {
      setCommenting(false);
    }
  };

  const handleResolveDispute = async (disputeId: string, resolution: string) => {
    if (!user?.uid) return;
    try {
      await resolveDispute(disputeId, resolution, user.uid);
      toast({ title: "Dispute Resolved" });
    } catch (error) {
      toast({ variant: "destructive", title: "Error" });
    }
  };

  const handleAttachUser = async () => {
    if (!user?.uid || !attachUserName || !attachRole) return;
    setAttaching(true);
    const isNewUser = !attachUserId;
    try {
      let uid = attachUserId;
      if (!uid) {
        // No registered account picked — create a claimable ghost user (same
        // mechanism as contributor placeholders) so the credit can be
        // attached NOW and claimed by the real person when they join BB.
        const ghost = await createPlaceholderContributor(attachUserName);
        uid = ghost.id;
      }
      await adminAttachUserToMedia(
        mediaId, mediaType, mediaTitle,
        uid, attachUserName, undefined,
        attachRole, attachSplit ? parseFloat(attachSplit) : undefined,
        user.uid, userProfile?.displayName || 'Admin',
      );
      toast({
        title: "User Attached",
        description: isNewUser
          ? `${attachUserName} was added as a new claimable user and attached — they can claim the account when they join.`
          : `${attachUserName} has been attached. They need to approve.`,
      });
      setShowAttachDialog(false);
      setAttachUserId('');
      setAttachUserName('');
      setAttachRole('');
      setAttachSplit('');
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to attach user." });
    } finally {
      setAttaching(false);
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'pending': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'rejected': return 'bg-red-500/10 text-red-600 border-red-500/20';
      case 'disputed': return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
      default: return '';
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-4">
      {/* Action Buttons — Claim Credits routes through the SHARED ClaimCreditsButton
          so the dialog is identical here and on the release-page header
          (per-track picker + play button + same role/split/notes). One window,
          one source of truth — no more two-dialogs-with-different-fields drift. */}
      <div className="flex flex-wrap gap-2">
        <ClaimCreditsButton
          mediaId={mediaId}
          mediaType={mediaType}
          mediaTitle={mediaTitle}
          tracks={tracks}
        />

        {isAdmin && (
          <Dialog open={showAttachDialog} onOpenChange={setShowAttachDialog}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1">
                <UserPlus className="h-3.5 w-3.5" /> Attach User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Attach User to Media</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-sm text-muted-foreground">
                  As an admin, attach a user to this media. They will need to approve the credit.
                </p>
                {/* Searchable user picker — types resolve against the
                    `users` collection (displayName, email, uid). Picking
                    a match autofills BOTH the User ID and Display Name
                    fields. Typing a raw UID still works for ghost users
                    that haven't been resolved yet. Per Gordon: "it should
                    search USER ID when attach users ID, everything
                    centralized database please." */}
                <div>
                  <Label>User</Label>
                  <UserSearchInput
                    value={attachUserName || attachUserId}
                    onPick={(u) => {
                      setAttachUserId(u.uid);
                      setAttachUserName(u.displayName || u.email || u.uid);
                    }}
                    onChangeText={(raw) => {
                      // Free-text edits update name; the user has to pick
                      // from the dropdown to also bind the UID.
                      setAttachUserName(raw);
                      // If they paste what looks like a 28-char Firebase
                      // UID, treat it as the UID directly.
                      if (/^[A-Za-z0-9]{20,}$/.test(raw.trim())) {
                        setAttachUserId(raw.trim());
                      }
                    }}
                    placeholder="Search by name, email, or paste UID…"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {attachUserId
                      ? <>Bound UID: <code>{attachUserId}</code></>
                      : <>No registered user matched — attaching will create a <b>new claimable user</b> they can claim when they join.</>}
                  </p>
                </div>
                <div>
                  <Label>Role</Label>
                  <select
                    value={attachRole}
                    onChange={e => setAttachRole(e.target.value)}
                    className="w-full mt-1 p-2 border rounded-md bg-background text-foreground"
                  >
                    <option value="">Select a role...</option>
                    {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Split % (optional)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="e.g., 50"
                    value={attachSplit}
                    onChange={e => setAttachSplit(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleAttachUser} disabled={!attachUserName.trim() || !attachRole || attaching}>
                  {attaching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Shield className="h-4 w-4 mr-1" />}
                  {attachUserId ? "Attach User" : "Attach as New User"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Credits List */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : credits.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Claimed Credits</h4>
          {credits.map(credit => (
            <div key={credit.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              {/* Avatar + name both link to the user's profile when we
                  have a userId. Per Gordon: "all credits as well, to the
                  user page." Falls through to a plain block when the
                  credit is unclaimed / unattached. */}
              {credit.userId ? (
                <Link href={`/profile/${credit.userId}`} className="contents">
                  <Avatar className="h-9 w-9">
                    {credit.userAvatar && <AvatarImage src={credit.userAvatar} />}
                    <AvatarFallback className="text-xs" name={credit.userName}>{getInitials(credit.userName)}</AvatarFallback>
                  </Avatar>
                </Link>
              ) : (
                <Avatar className="h-9 w-9">
                  {credit.userAvatar && <AvatarImage src={credit.userAvatar} />}
                  <AvatarFallback className="text-xs" name={credit.userName}>{getInitials(credit.userName)}</AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1 min-w-0">
                {credit.userId ? (
                  <Link href={`/profile/${credit.userId}`} className="text-sm font-medium truncate block hover:text-primary hover:underline transition-colors">{credit.userName}</Link>
                ) : (
                  <p className="text-sm font-medium truncate">{credit.userName}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{credit.role}</span>
                  {credit.splitPercent != null && (
                    <span className="flex items-center gap-0.5"><Percent className="h-3 w-3" />{credit.splitPercent}%</span>
                  )}
                </div>
              </div>
              <Badge variant="outline" className={cn("text-[10px]", statusColor(credit.status))}>
                {credit.status}
              </Badge>
              <div className="flex gap-1">
                {/* Show approve/reject if this credit is for the current user and pending */}
                {credit.userId === user?.uid && credit.status === 'pending' && (
                  <>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleApprove(credit.id!)}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleReject(credit.id!)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                {/* Admin can approve/reject any */}
                {isAdmin && credit.status === 'pending' && credit.userId !== user?.uid && (
                  <>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => handleApprove(credit.id!)}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => handleReject(credit.id!)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                {/* Any affiliated user can dispute */}
                {credit.status !== 'disputed' && credit.status !== 'rejected' && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-orange-600" onClick={() => {
                    setDisputeCreditId(credit.id!);
                    setShowDisputeDialog(true);
                  }}>
                    <AlertTriangle className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Disputes List */}
      {disputes.length > 0 && (
        <div className="space-y-2 mt-4">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Disputes
          </h4>
          {disputes.map(dispute => {
            const isExpanded = expandedDispute === dispute.id;
            return (
              <div key={dispute.id} className="rounded-lg border bg-card overflow-hidden">
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary/30"
                  onClick={() => setExpandedDispute(isExpanded ? null : dispute.id!)}
                >
                  <AlertTriangle className={cn("h-4 w-4 flex-shrink-0", dispute.status === 'open' ? 'text-orange-500' : 'text-muted-foreground')} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">Dispute by {dispute.disputedByName}</p>
                    <p className="text-xs text-muted-foreground truncate">{dispute.reason}</p>
                  </div>
                  <Badge variant="outline" className={cn("text-[10px]", dispute.status === 'open' ? 'text-orange-600' : 'text-green-600')}>
                    {dispute.status}
                  </Badge>
                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
                {isExpanded && (
                  <div className="border-t p-3 space-y-3">
                    <p className="text-sm">{dispute.reason}</p>
                    {dispute.proposedRole && <p className="text-xs text-muted-foreground">Proposed role: <strong>{dispute.proposedRole}</strong></p>}
                    {dispute.proposedSplitPercent != null && <p className="text-xs text-muted-foreground">Proposed split: <strong>{dispute.proposedSplitPercent}%</strong></p>}

                    {/* Comments */}
                    {dispute.comments && dispute.comments.length > 0 && (
                      <div className="space-y-2 pl-3 border-l-2 border-border">
                        {dispute.comments.map((comment: any) => (
                          <div key={comment.id} className="flex gap-2">
                            <Avatar className="h-6 w-6 flex-shrink-0">
                              {comment.userAvatar && <AvatarImage src={comment.userAvatar} />}
                              <AvatarFallback className="text-[10px]" name={comment.userName}>{getInitials(comment.userName)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-xs"><strong>{comment.userName}</strong></p>
                              <p className="text-xs text-muted-foreground">{comment.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add comment */}
                    {dispute.status === 'open' && (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a comment..."
                          value={commentText}
                          onChange={e => setCommentText(e.target.value)}
                          className="text-sm"
                          onKeyDown={e => { if (e.key === 'Enter') handleAddComment(dispute.id!); }}
                        />
                        <Button size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => handleAddComment(dispute.id!)} disabled={commenting || !commentText.trim()}>
                          {commenting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    )}

                    {/* Resolve (admin or affiliated users) */}
                    {dispute.status === 'open' && (isAdmin || credits.some(c => c.userId === user?.uid)) && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleResolveDispute(dispute.id!, 'Accepted by parties')}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleResolveDispute(dispute.id!, 'Rejected')}>
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    )}

                    {dispute.resolution && (
                      <p className="text-xs bg-muted p-2 rounded"><strong>Resolution:</strong> {dispute.resolution}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dispute Dialog */}
      <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>File a Dispute</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Reason for Dispute</Label>
              <Textarea
                placeholder="Explain why you're disputing this credit, split, or role..."
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label>Proposed Role (optional)</Label>
              <select
                value={disputeProposedRole}
                onChange={e => setDisputeProposedRole(e.target.value)}
                className="w-full mt-1 p-2 border rounded-md bg-background text-foreground"
              >
                <option value="">Keep current / No change</option>
                {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label>Proposed Split % (optional)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 33"
                value={disputeProposedSplit}
                onChange={e => setDisputeProposedSplit(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreateDispute} disabled={!disputeReason || disputing} variant="destructive">
              {disputing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
              File Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
